<script lang="ts" setup>
import { computed } from 'vue';

import {
  AppImagesLoader,
  AppLazyLoadComponent,
  AppSvgIcon,
  AppWhiteDot,
} from '@libs/ui';

import type { FlightDetailsModalSegment } from './flightDetailsModal.model';

interface FlightSegmentProps {
  segment: FlightDetailsModalSegment;
  imageHandler: (src: string) => void;
}

const props = defineProps<FlightSegmentProps>();

const hasOperatingCarrier = computed<boolean>(
  () => props.segment.operatingCarrier !== props.segment.carrier?.name
);

function getLogoUrls(): string[] {
  const urls: string[] = [];

  if (props.segment.carrier?.logo) {
    urls.push(props.segment.carrier.logo);
  }

  return urls;
}
</script>

<template>
  <div class="flight-segment">
    <div class="flight-segment-timeline">
      <div>
        <AppWhiteDot :size="8" />
      </div>

      <div>
        <AppWhiteDot :size="8" />
      </div>
    </div>

    <div class="flight-segment-details">
      <div class="destination">
        <div class="time">{{ props.segment.departureDate }}</div>
        <div class="airport">{{ props.segment.departureAirport }}</div>
      </div>

      <div class="summary">
        <div class="duration">
          {{ props.segment.duration }}
        </div>
        <div class="flightNumber separator">
          {{ `${props.segment.carrier?.name} ${props.segment.flightNumber} ` }}
        </div>
        <div class="airline-summary">
          <div class="airline">
            <AppLazyLoadComponent class="logo">
              <AppImagesLoader
                :urls="getLogoUrls()"
                :alt="$t('a11y.flight_carrier_logo')"
                img-type="contain"
                @images-loader-error="props.imageHandler"
              >
                <AppSvgIcon name="airplane" size="24" />
              </AppImagesLoader>
            </AppLazyLoadComponent>
          </div>
          <div class="airline-name">{{ props.segment.carrier?.name }}</div>
          <div v-if="hasOperatingCarrier">
            {{
              ` (${$t('planner.operated_by')} ${
                props.segment.operatingCarrier
              })`
            }}
          </div>
        </div>
      </div>

      <div class="destination">
        <div class="time">{{ props.segment.arrivalDate }}</div>
        <div class="airport">{{ props.segment.arrivalAirport }}</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.flight-segment {
  display: flex;
  gap: var(--spacing-16);

  .flight-segment-timeline {
    --dot-size: 8px;
    --timeline-width: 14px;

    display: flex;
    flex-flow: column;
    align-items: center;
    justify-content: space-between;

    background: var(--color-neutral-30);
    border-radius: var(--border-radius-8);
    width: var(--timeline-width);
    padding: 3px 0;
  }

  .flight-segment-details {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-16);
    flex-grow: 1;

    .destination {
      .time {
        @include font-label-xl-bold;
      }

      .airport {
        padding-top: var(--spacing-4);
        @include font-label-m-light;
      }
    }

    .summary {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: var(--spacing-8);
      align-items: center;
      color: var(--color-neutral-70);
      @include font-label-m-light;

      .airline-summary {
        display: flex;
        gap: var(--spacing-8);
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }

      .airline {
        height: var(--spacing-24);
      }

      .separator {
        border-left: 1px solid var(--color-neutral-70);
        padding-left: var(--spacing-8);
      }
    }
  }
}
</style>
